import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, Dialog, Input, Price } from '@artosera/ui';
import { useState } from 'react';
import { estApiError } from '../../api/errors.js';
import {
  useSuspensionPaiement,
  type EncaissementPaiement,
  type RelancePaiement,
  type TentativePaiement,
} from '../../api/paiementEnLigne.js';
import type { Pagination } from '../../api/types.js';
import { Field } from '../Field.js';
import styles from './PaiementEnLigne.module.css';

export function useTextePaiement() {
  const { t, i18n } = useTranslation(['regie', 'commun', 'erreurs']);
  return {
    t,
    label: (cle: string) => t(`regie:paiementEnLigne.${cle}`),
    langue: i18n.language === 'en' ? ('en' as const) : ('fr' as const),
  };
}
export function ErreurPaiement({ erreur, factureUrl }: { erreur: unknown; factureUrl?: string }) {
  const { t, label } = useTextePaiement();
  if (!erreur) return null;
  let code = '';
  if (estApiError(erreur)) {
    code = erreur.code;
    if (
      typeof erreur.details === 'object' &&
      erreur.details !== null &&
      'raison' in erreur.details &&
      typeof erreur.details.raison === 'string'
    )
      code = erreur.details.raison;
  }
  const message = t(`erreurs:${code}`, {
    defaultValue: estApiError(erreur) ? erreur.message : t('commun:erreurReseau'),
  });
  return (
    <p role="alert">
      {message}{' '}
      {code === 'paiement_en_ligne.avoir_requis' && factureUrl && (
        <Link to={factureUrl}>{label('voirFacture')}</Link>
      )}
    </p>
  );
}
export function PagesPaiement({
  pagination,
  changer,
}: {
  pagination?: Pagination;
  changer: (page: number) => void;
}) {
  const { label } = useTextePaiement();
  if (!pagination || pagination.pages <= 1) return null;
  return (
    <nav className={styles.actions} aria-label={label('pagination')}>
      <Button disabled={pagination.page <= 1} onClick={() => changer(pagination.page - 1)}>
        {label('precedent')}
      </Button>
      <span>
        {pagination.page} / {pagination.pages}
      </span>
      <Button
        disabled={pagination.page >= pagination.pages}
        onClick={() => changer(pagination.page + 1)}
      >
        {label('suivant')}
      </Button>
    </nav>
  );
}
export function TentativesPaiement({ tentatives }: { tentatives: TentativePaiement[] }) {
  const { label, langue } = useTextePaiement();
  return (
    <section className={styles.section}>
      <h3>{label('tentatives')}</h3>
      {tentatives.length === 0 && <p>{label('aucun')}</p>}
      <ul>
        {tentatives.map((v) => (
          <li key={v._id}>
            {v.createdAt.slice(0, 10)} · {label(`etats.${v.etat}`)} ·{' '}
            <Price cents={v.montantCentimes} currency={v.devise} lang={langue} />
            {v.codeEchec && <> · {label(`etats.${v.codeEchec}`)}</>}
          </li>
        ))}
      </ul>
    </section>
  );
}
export function EncaissementsPaiement({
  encaissements,
  galerieSlug,
}: {
  encaissements: EncaissementPaiement[];
  galerieSlug: string;
}) {
  const { label, langue } = useTextePaiement();
  return (
    <>
      <ul>
        {encaissements.map((v) => (
          <li key={v._id}>
            <Link to={`/${galerieSlug}/paiement-en-ligne/encaissements/${v._id}`}>
              {v.payeLe.slice(0, 10)} · {label(`etats.${v.etat}`)}
            </Link>{' '}
            · <Price cents={v.montantCentimes} currency={v.devise} lang={langue} />
          </li>
        ))}
      </ul>
      {encaissements.length === 0 && <p>{label('aucun')}</p>}
    </>
  );
}
export function HistoriqueRelances({
  relances,
  galerieSlug,
}: {
  relances: RelancePaiement[];
  galerieSlug: string;
}) {
  const { label, langue } = useTextePaiement();
  return (
    <>
      <ul>
        {relances.map((v) => (
          <li key={v._id}>
            <Link to={`/${galerieSlug}/ventes/${v.factureId}`}>{v.factureId}</Link> ·{' '}
            {label('palier')} {v.rang} · {v.destinataire} · {label(`etats.${v.etat}`)} ·{' '}
            <Price cents={v.resteDuCentimes} lang={langue} />
          </li>
        ))}
      </ul>
      {relances.length === 0 && <p>{label('aucun')}</p>}
    </>
  );
}
export function SuspensionPaiement({
  galerieSlug,
  factureId,
  suspendues,
}: {
  galerieSlug: string;
  factureId: string;
  suspendues: boolean;
}) {
  const { t, label } = useTextePaiement();
  const mutation = useSuspensionPaiement(galerieSlug);
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const titre = label(suspendues ? 'reprendreRelances' : 'suspendreRelances');
  return (
    <>
      <Button
        onClick={() => {
          setMotif('');
          mutation.reset();
          setOuvert(true);
        }}
      >
        {titre}
      </Button>
      <Dialog open={ouvert} onOpenChange={setOuvert} title={titre} closeLabel={t('commun:fermer')}>
        <form
          className={styles.formulaire}
          onSubmit={(e) => {
            e.preventDefault();
            void mutation
              .mutateAsync({ id: factureId, suspendues: !suspendues, motif: motif.trim() })
              .then(() => setOuvert(false))
              .catch(() => {});
          }}
        >
          <Field label={label('motif')} htmlFor={`motif-${factureId}`}>
            <Input
              id={`motif-${factureId}`}
              required
              maxLength={1000}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </Field>
          <ErreurPaiement erreur={mutation.error} />
          <Button type="submit" loading={mutation.isPending} disabled={!motif.trim()}>
            {label('confirmer')}
          </Button>
        </form>
      </Dialog>
    </>
  );
}
